import _ from 'lodash';
import XLSX from 'xlsx';
import { Parser } from 'json2csv';
import iconv from 'iconv-lite';
import queryString from 'query-string';
import ClipboardJS from 'clipboard';
import { i18n_t } from '@/locales/i18n';
import ipc from '@/renderer/scripts/utils/ipc';
import {
  IS_DEV,
  IS_DEV_PUBLISH,
  IS_TEST,
  IS_PRE,
  IS_PRIVATE,
  platform,
  IS_BROWSER,
  IS_BROWSER_IN_MOBILE,
  IS_PC,
} from '@/common/constant/platform';
import { SMH } from '@/renderer/scripts/smh';
import { IpcEvents } from '@/ipc-events';

import store from '@/renderer/store';
import { CONSTANT_URL } from '@/renderer/scripts/http';
import { COLOR_GROUP, TEXT_COLOR_GROUP } from '../ConstantConfig';
import tips from '../tips';
export { encodeText, decodeText } from '@/renderer/scripts/utils/tools/packages/encrypt';
export { calcRemainingTime } from '@/renderer/scripts/utils/tools/packages/transmission';
export * from '@/renderer/scripts/utils/tools/packages/check';

type FormatSize = {
  size: number;
  unit: string;
};
type ShowLogObj<T extends 'SMH' | 'COS' | string> = { [key in T]: string } & {
  METHOD: string;
  URL: string;
  QUERY: string;
  BODY: string;
  START?: string;
  END?: string;
  COST?: string;
  DATA?: string;
  ERROR?: string;
  HEADERS?: string;
  PROXY?: string;
};
let fs: typeof import('fs');
let path: typeof import('path');
let nodeXlsx: null | any = null;
if (IS_PC) {
  fs = (window.parent as any).require('fs') as typeof import('fs');
  path = (window.parent as any).require('path') as typeof import('path');
  nodeXlsx = (window.parent as any).require('node-xlsx');
}

/**
 * 格式化单位 B转MB、GB 10240 => 1,KB；round：true=整数格式，false=小数点格式
 * @param originSize 数据大小 单位byte
 * @param round 转义精度（true整数、false小数）
 * @param unitSimple 简单单位（K/M/G/T/P/E）
 * @returns {size:number,unit:string}
 */
export function formatSize(originSize: string | number | null, round?: boolean, unitSimple?: boolean): FormatSize {
  if (!originSize || Number.isNaN(+originSize)) {
    return {
      size: 0,
      unit: 'B',
    };
  }
  const negative = +originSize < 0;
  let size: number = parseFloat(`${originSize}`);
  // 兼容负数，先转正数做计算
  if (negative) {
    size = 0 - size;
  }
  let unitVal = 'B';
  let unit = ['KB', 'MB', 'GB', 'TB', 'PB', 'EB'];
  if (unitSimple) {
    unit = ['K', 'M', 'G', 'T', 'P', 'E'];
  }
  let index = 0;
  if (round) {
    for (; index < unit.length && size >= 1024; index += 1) {
      size /= 1024;
      if (!Number.isInteger(size)) {
        size *= 1024;
        break;
      }
      unitVal = unit[index];
    }
  } else {
    for (; index < unit.length && size >= 1024; index += 1) {
      size /= 1024;
      unitVal = unit[index];
    }
  }
  let sizeVal = Number.isInteger(size) ? size : Number(size.toFixed(2));
  // 兼容负数，计算完再转为负数
  if (negative) {
    sizeVal = 0 - sizeVal;
  }
  return {
    // 考虑容量显示，和产品确认保留两位小数
    size: sizeVal,
    unit: unitVal,
  };
}

// 大小格式化 (1, 'MB') => 1024 * 1024
export function formatSize2Byte(size: string | number, unit: string): number {
  const unitList = ['KB', 'MB', 'GB', 'TB', 'PB', 'EB'];
  const i = unitList.indexOf(unit);
  const sizePow = 1024 ** (i + 1);
  const sizeStr = +size * sizePow;
  return sizeStr;
}

// 大小格式化,转换为指定单位 (100 * 1024 * 1024, 'MB') => 100
export function formatSize2Unit(size: string | number, unit: string): number {
  const unitList = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB'];
  const unitIndex = unitList.indexOf(unit);
  const formatSize = +size / 1024 ** unitIndex;
  return +formatSize.toFixed(1);
}
export function formatType(type: string): string {
  let formatType = '';
  switch (type) {
    case 'dir':
      formatType = i18n_t('文件夹');
      break;
    case 'file':
      formatType = i18n_t('文件');
      break;
    case 'image':
      formatType = i18n_t('图片');
      break;
    case 'video':
      formatType = i18n_t('视频');
      break;
    default:
      formatType = type;
      break;
  }

  return formatType;
}

const IconMap: {
  [key: string]: string;
} = {
  image: 'jpeg,jpg,png,gif,svg,apng,webp,ico,bmp,psd,tif,heic,heif,mac',
  word: 'doc,dot,wps,wpt,docx,dotx,docm,dotm,rtf,mht',
  audio: 'mp3,wav,aac,flac,amr,m4a,wma,ogg,ape,cda,rma,mid,midi,aiff,au,mod,xmi,rmi,cd,flag',
  video: 'avi,mp4,mkv,flv,hls,ts,rmvb,mtv,mov,vcd,dat,mpeg,mpg,rm,ra,asf,wmv,swf,3gp,amv,dmv,qt',
  portable: 'pdf,xps',
  powerpoint: 'pptx,ppt,pot,potx,pps,ppsx,dps,dpt,pptm,potm,ppsm,keynote',
  text: 'txt,html,yml,xhtml,htm,php,js,css,vue,md',
  excel: 'xls,xlt,et,ett,xlsx,xltx,csv,xlsb,xlsm,xltm,ets',
  archive: 'zip,rar,gz,tar,iso,dmg,7z,bz,bz2,cab',
  other: '',
};
export function getFileIcon(filename: string, fileType?: string): string {
  if (!fileType) {
    const index: number = filename?.lastIndexOf('.');
    const ext: string = index > -1 ? filename?.toLocaleLowerCase()?.substr(index + 1) : '';
    let type = 'other';
    const iconTypes = Object.keys(IconMap);
    for (let i = 0; i < iconTypes.length; i++) {
      const key = iconTypes[i];
      const value = IconMap[key];
      if (value.includes(ext)) {
        type = key;
      }
    }
    return type;
  }
  return fileType;
}
function formatHeaders(headers: Record<string, string>): Record<string, string> {
  const Headers: Record<string, string> = {};
  if (Headers && typeof Headers === 'object') {
    const headerArr = [
      // header 标准头部
      'Content-MD5',
      'Content-Length',
      'Content-Type',
      'Expect',
      'Expires',
      'Cache-Control',
      'Content-Disposition',
      'Content-Encoding',
      'Range',
      'Accept-Ranges',
      'Date',
      'Accept-TCED-Language',
      // SMH 有关头部
      'x-request-id',
      'x-cos-request-id',
      'Authorization',
      'x-cos-security-token',
      'x-amz-content-sha256',
      'x-amz-date',
      'x-amz-hash-crc64ecma',
      'x-amz-request-id',
    ];

    headerArr.forEach(key => {
      if (headers[key] !== undefined) {
        Headers[key] = headers[key];
      }
      const lowerCaseKey = key.toLowerCase();
      if (headers[lowerCaseKey] !== undefined) {
        Headers[lowerCaseKey] = headers[lowerCaseKey];
      }
    });
  }
  return Headers;
}

/**
 * @description 给 COS 打日志（测试）
 * @param {*} err
 * @param {*} data
 * @param {*} config
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const showLog = (api: 'COS' | 'SMH' | string, err: any, data: any, config: any): string[] | null => {
  if (IS_BROWSER) return null;
  if (err?.code?.toLowerCase?.().includes('cancel')) return null;
  try {
    const apiName = config.customAttributes ? config.customAttributes.apiName : '';
    const [URL] = config.url?.split('?');
    const { query: queryFromUrl = {} } = getUrlQuery(config.url);
    const safeQuery = Object.assign(queryFromUrl, _.cloneDeep(config.params || {}));

    /**
     * FIX: 私有化 userToken 和 accesstoken 打印32个字符，公有云打印16个字符
     */
    safeQuery.user_token &&
      (safeQuery.user_token = IS_PRIVATE ? safeQuery.user_token.slice(0, 32) : safeQuery.user_token.slice(0, 16));
    safeQuery.access_token &&
      (safeQuery.access_token = IS_PRIVATE ? safeQuery.access_token.slice(0, 32) : safeQuery.access_token.slice(0, 16));
    const QUERY = JSON.stringify(safeQuery);
    const isValidBody = config.data && (typeof config.data === 'string' || !config.data.pipe);
    const BODY = JSON.stringify(isValidBody ? config.data : {});
    const showLogObj: ShowLogObj<typeof api> = {
      [api]: apiName,
      METHOD: config.method,
      URL,
      QUERY,
      BODY,
    };
    if (config?.proxy !== undefined) showLogObj.PROXY = JSON.stringify(config?.proxy);
    if (config?.httpAgent?.options !== undefined) showLogObj.HTTPAGENT = JSON.stringify(config?.httpAgent?.options);
    if (config?.httpsAgent?.options !== undefined) showLogObj.HTTPSAGENT = JSON.stringify(config?.httpsAgent?.options);

    // 请求时头部
    showLogObj.RequesHeaders = JSON.stringify(formatHeaders(config.headers));
    // 响应时头部
    if ((data && data?.headers) || (err && err?.headers)) {
      const headers = formatHeaders(data?.headers || err?.headers);
      showLogObj.ResponseHeaders = JSON.stringify(headers);
    }
    const ERROR = _.cloneDeep(err);
    const DATA = _.cloneDeep(data);
    if (DATA?.data?.userToken) {
      DATA.data.userToken = IS_PRIVATE ? DATA.data.userToken.slice(0, 32) : DATA.data.userToken.slice(0, 16);
    }
    if (DATA?.data?.accessToken) {
      DATA.data.accessToken = IS_PRIVATE ? DATA.data.accessToken.slice(0, 32) : DATA.data.accessToken.slice(0, 16);
    }
    if (ERROR?.headers) delete ERROR.headers;
    if (ERROR?.config) delete ERROR.config;
    if (DATA?.headers) delete DATA.headers;
    if (config.starttime && config.endtime) {
      showLogObj.ERROR = JSON.stringify(ERROR);
      try {
        showLogObj.DATA = JSON.stringify(DATA);
      } catch (error) {
        delete DATA.data; // 当DATA.data是一个流时会报错，因此在这进行删除以免无法正确打印
        showLogObj.DATA = JSON.stringify(DATA);
      }
      showLogObj.START = config.starttime;
      showLogObj.END = config.endtime;
      showLogObj.COST = `${config.endtime - config.starttime}ms`;
    } else if (!config.starttime && ERROR) {
      showLogObj.ERROR = JSON.stringify(ERROR);
    }
    const logParams = Object.keys(showLogObj).map(key => {
      const value = showLogObj[key];
      if (!value) return `[${key}]`;
      return `[${key}: ${value}]`;
    });
    return logParams;
  } catch (error) {
    console.log('[Format log] error', error);
    return null;
    // logger.smh.error('wonder_log ~ showLog ~ error', error);
  }
};

// 用于判断节点内容是否溢出
export const isNodeOverflow = (ele: Vue | Element | (Vue | Element)[]): boolean => {
  const { clientWidth = 0, scrollWidth = 0 } = ele as Element & {
    clientWidth: number;
    scrollWidth: number;
  };
  if (scrollWidth > clientWidth) {
    return true;
  }
  return false;
};

/**
 * @description 字符串是否全中文判断
 * @param {string} str
 * @return {*}  {boolean}
 */
export function isChinese(str: string): boolean {
  const re = /[^\u4e00-\u9fa5]/;
  if (re.test(str)) return false;
  return true;
}

/**
 * @description 字符串是否是emoji、需要和for of循环一块使用,这样表情不会拆成两个字符
 * @param {string} str
 * @return {*}  {boolean}
 */
export function isEmoji(str: string): boolean {
  return (str.codePointAt(0) as number) > 0xffff;
}

/**
 * @description 字符是否为大写英文判断
 * @param {string} char
 * @return {*}  {boolean}
 */
export function isUpperCase(char: string): boolean {
  const re = /^[A-Z]$/;
  return re.test(char);
}

// 通过a标签下载
export const downloadByLink = (href: string | object, name: string, blank = false): void => {
  let url = href;
  if (typeof href === 'object' && href instanceof Blob) {
    url = URL.createObjectURL(href); // 创建blob地址
  }

  // 使用responseType = 'blob'的方式绕过跨域导致重命名失败的问题
  const x = new XMLHttpRequest();
  x.open('GET', url as string, true);
  x.responseType = 'blob';
  x.onload = () => {
    const url = window.URL.createObjectURL(x.response);
    const link = document.createElement('a');
    blank && link.setAttribute('target', '_blank');
    link.href = url as string;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  x.send();
};
function sheet2blob(sheet: object, sheetName = 'sheet1') {
  const workbook = {
    SheetNames: [sheetName],
    Sheets: {
      [sheetName]: sheet,
    },
  };
  // 字符串转ArrayBuffer
  const s2ab = (s: string) => {
    const buf = new ArrayBuffer(s.length);
    const view = new Uint8Array(buf);
    for (let i = 0; i !== s.length; ++i) {
      // eslint-disable-next-line no-bitwise
      view[i] = s.charCodeAt(i) & 0xff;
    }
    return buf;
  };
  // 生成excel的配置项
  const wopts: any = {
    // 要生成的文件类型
    bookType: 'xlsx',
    // 是否生成Shared String Table，官方解释是，如果开启生成速度会下降，但在低版本IOS设备上有更好的兼容性
    bookSST: false,
    type: 'binary',
  };
  const wbout = XLSX.write(workbook, wopts);
  const blob = new Blob([s2ab(wbout)], {
    type: 'application/octet-stream',
  });
  return blob;
}

// 表格数据导出excel web端
export const exportExcelInBrowser = (columns: any[], records: any[], exportName = i18n_t('导出.xlsx')): void => {
  /*
   * 数据结构
   * columns = ['name', 'age'];
   * records = [['张三', 20], ['李四', 18]]
   */
  const sheet = XLSX.utils.aoa_to_sheet([columns, ...records]);
  downloadByLink(sheet2blob(sheet), exportName);
};

// 表格数据导出excel pc端
export const exportExcelInPC = (
  columns: any[],
  records: any[],
  exportName = i18n_t('导出.xlsx'),
  downloadTarget: string,
): void => {
  /*
   * 数据结构
   * columns = ['name', 'age'];
   * records = [['张三', 20], ['李四', 18]]
   */
  const datas = [columns, ...records];
  const buffer = nodeXlsx.build([
    {
      name: 'sheet1',
      data: datas,
    },
  ]);

  const lastIndex = exportName.lastIndexOf('.');
  const ext = exportName.slice(lastIndex);
  const fileName = exportName.slice(0, lastIndex);
  let savePath = path.resolve(downloadTarget, `${fileName}${ext}`);
  // 文件已存在需要重命名
  let i = 1;
  while (i <= 1000000 && fs.existsSync(savePath)) {
    savePath = path.resolve(downloadTarget, `${fileName} (${i})${ext}`);
    i++;
  }
  try {
    fs.writeFileSync(savePath, buffer);
    ipc.send(IpcEvents.ON_OPERATE_MAIN_LISTENER, {
      event: 'openPath',
      params: {
        path: savePath,
      },
    });
  } catch (e) {
    tips.error(i18n_t('导出失败'));
  }
};
export const Json2Csv = (columns: any[], records: any[]): string => {
  const fields = columns.map(item => ({
    ...item,
    label: item.title,
    value: item.colKey,
  }));
  const json2csvParser = new Parser({
    fields,
    excelStrings: true,
  });
  const result = json2csvParser.parse(records);
  return result;
};

/**
 * 标准表格数据导出csv 浏览器端
 * @param columns
 * @param records
 * @param exportName
 * @returns 是否成功
 */
export const exportCSVInBrowser = (columns: any[], records: any[], exportName = i18n_t('导出.csv')): boolean => {
  try {
    const result = Json2Csv(columns, records);
    // IE10或Edge浏览器
    const isIE =
      (navigator.userAgent.indexOf('compatible') > -1 && navigator.userAgent.indexOf('MSIE') > -1) ||
      navigator.userAgent.indexOf('Edge') > -1;
    if (IS_BROWSER && isIE) {
      const BOM = '\uFEFF';
      const csvData = new Blob([BOM + result], {
        type: 'text/csv',
      });
      if (!(navigator as any).msSaveBlob) {
        tips.error(i18n_t('浏览器不支持，请升级后重试'));
        return false;
      }
      (navigator as any).msSaveBlob(csvData, exportName);
    } else {
      // 非IE浏览器
      const csvContent = `data:text/csv;charset=utf-8,\uFEFF${result}`;
      // 使用a标签的download属性实现下载功能
      downloadByLink(encodeURI(csvContent), exportName);
    }
    return true;
  } catch (err) {
    tips.error(i18n_t('导出失败'));
    return false;
  }
};

/**
 * 标准表格数据导出csv pc端
 * @param columns
 * @param records
 * @param exportName
 * @param downloadTarget
 * @returns 是否成功
 */
export const exportCSVInPC = (
  columns: any[],
  records: any[],
  exportName = i18n_t('导出.csv'),
  downloadTarget: string,
): boolean => {
  const lastIndex = exportName.lastIndexOf('.');
  const ext = exportName.slice(lastIndex);
  const fileName = exportName.slice(0, lastIndex);
  const csvContent = Json2Csv(columns, records);
  // 使用通用的utf-8格式 且 增加bom标记告知excel打开时应该以utf-8格式编码
  const bom = '\uFEFF';
  const iconvResult = iconv.encode(bom + csvContent, 'utf-8');
  let savePath = path.resolve(downloadTarget, `${fileName}${ext}`);
  // 文件已存在需要重命名
  let i = 1;
  while (i <= 1000000 && fs.existsSync(savePath)) {
    savePath = path.resolve(downloadTarget, `${fileName} (${i})${ext}`);
    i++;
  }
  try {
    fs.writeFileSync(savePath, iconvResult);
    ipc.send(IpcEvents.ON_OPERATE_MAIN_LISTENER, {
      event: 'openPath',
      params: {
        path: savePath,
      },
    });
    return true;
  } catch (e) {
    tips.error(i18n_t('导出失败'));
    return false;
  }
};
export const getFileExt = (name: string): string => {
  if (name) {
    const index = name.lastIndexOf('.');
    const ext = index > -1 ? name.substr(index + 1) : '';
    return ext;
  }
  return '';
};

/**
 * @description 根据 userId 获取头像默认颜色
 * @export
 * @param {number} userId
 * @return {*}  {obj}
 */
// 头像默认背景颜色和文字颜色
export function getDefaultColorByUserId(userId: number): Record<string, string> {
  const index = userId && userId > -1 ? userId % 3 : 0;
  const colorObj = {
    bgColor: COLOR_GROUP[index],
    textColor: TEXT_COLOR_GROUP[index],
  };
  return colorObj;
}

// 格式化url参数
export function getQueryParam(name: string, url: string): string {
  const { query } = queryString.parseUrl(url);
  return query[name] as string;
}

// 获取url所有参数
export function getUrlQuery(url: string): {
  query: any;
  url: string;
} {
  return queryString.parseUrl(url);
}

// 从类似2.1.1-1000获取三位版本号
export function getVersionFromVersionString(name: string) {
  const list = name.split('-');
  if (list.length <= 1) {
    return name;
  }
  return list[0];
}
// 从类似2.1.1-1000获取1000
export function getChannelFromVersionString(name: string) {
  const list = name.split('-');
  if (list.length <= 1) {
    return '';
  }
  return list[1];
}
export function compareVersion(_curr: string, _latest: string): number {
  const curr = getVersionFromVersionString(_curr).split('.');
  const latest = getVersionFromVersionString(_latest).split('.');
  let level = 0;
  let update = 0;
  for (let i = 0; i < curr.length; i++) {
    if (curr[i] !== latest[i]) {
      level = i + 1;
      update = parseInt(curr[i], 10) > parseInt(latest[i], 10) ? -level : level;
      break;
    }
  }
  return update;
}

// 返回图片像素
export function getImgWidthHeight(imgFile: File): Promise<{
  width: number;
  height: number;
}> {
  const reader = new FileReader();
  reader.readAsDataURL(imgFile);
  return new Promise(resolve => {
    reader.onload = (e: any) => {
      const base64 = e.target.result;
      const img = new Image();
      img.src = base64;
      img.onload = (i: any) => {
        resolve({
          width: i.target.width,
          height: i.target.height,
        });
      };
    };
  });
}

export function getNameTipsDetail({ accountName = '', countryCode = '', phoneNumber = '' }) {
  let detail = '';
  if (store.getters.onlyPasswordLogin) {
    detail = accountName || i18n_t('未设置账号');
  } else {
    detail = phoneCompose(countryCode, phoneNumber, false);
  }
  return detail ? `（${detail}）` : '';
}

// 电话区号抽离判断
export function phoneCompose(countryCode = '+86', phoneNumber?: string, inactive?: boolean): string {
  if (typeof inactive !== 'undefined' && inactive) return i18n_t('未激活');
  if (!phoneNumber) return store.getters.allowPhoneLogin ? i18n_t('暂未绑定') : '';
  if (!countryCode || countryCode === '+86') {
    return phoneNumber;
  }
  return `${countryCode}-${phoneNumber}`;
}

// 在浏览器打开控制台页面
export function openConsoleInBrowser(target = '_blank'): void {
  const url = CONSTANT_URL.consoleManagementUrl;
  if (IS_BROWSER) {
    window.open(url, target);
  } else {
    ipc.send(IpcEvents.ON_OPERATE_MAIN_LISTENER, {
      event: 'openUrl',
      params: {
        url,
      },
    });
  }
}

// 获取文件目录字符串，【'a','b','c'】=> a/b/; ['a'] => '/'
export function getFilePath(path: string[], root?: string): string {
  let pathStr = '';
  if (path.length === 1) {
    pathStr = '/';
  } else {
    pathStr = `${path.slice(0, path.length - 1).join('/')}/`;
  }
  if (root) {
    // 防止出现双斜杠
    pathStr = [root, pathStr]
      .filter(v => v)
      .join('/')
      .replace(/\/\//, '/');
  }
  return pathStr;
}

export function getSpaceDirectory(data: any): string {
  let spaceStr = '';
  if (data.spaceTag === 'personal') {
    if (data.relatedToMePath) {
      spaceStr = i18n_t('{slot1}的个人空间', { slot1: data.user.name });
    } else {
      spaceStr = i18n_t('个人空间');
    }
  } else if (data.spaceTag === 'team') {
    spaceStr = i18n_t('企业空间/{slot1}', { slot1: data.team?.name });
  } else if (data.spaceTag === 'group') {
    spaceStr = i18n_t('协作空间/{slot1}', { slot1: data.group?.name });
  }
  return spaceStr;
}

// 计算一段时间内的工作日
const stringToDate = (dateString: string) => {
  const dateArray = dateString.split('-');
  return new Date(Number(dateArray[0]), Number(dateArray[1]) - 1, Number(dateArray[2]));
};
export function countWorkDay(date1: string, date2: string): number {
  let startDate = stringToDate(date1);
  const endDate = stringToDate(date2);
  let startDateStamp = Number(startDate);
  const endDateStamp = Number(endDate);
  const delta = (endDateStamp - startDateStamp) / (1000 * 60 * 60 * 24) + 1; // 计算出总时间

  let weeks = 0;
  for (let i = 0; i < delta; i++) {
    if (startDate.getDay() === 0 || startDate.getDay() === 6) weeks++; // 若为周六或周天则加1
    startDateStamp = startDate.valueOf();
    startDateStamp += 1000 * 60 * 60 * 24;
    startDate = new Date(startDateStamp);
  }
  return delta - weeks;
}
/**
 * 复制内容到剪切板
 * @param {String} text
 */
export function copyText2Clipboard(text: string): Promise<void> {
  let button = document.getElementsByClassName('copy-test')[0] as HTMLButtonElement;
  if (!button) {
    button = document.createElement('button');
    button.setAttribute('class', 'copy-test');
    button.setAttribute('style', 'display: none');
    document.body.appendChild(button);
  }
  button.dataset.clipboardText = text;
  const clipboard = new ClipboardJS('.copy-test');
  return new Promise<void>((resolve, reject) => {
    setTimeout(() => {
      button.click();
    }, 100);
    clipboard.on('success', e => {
      resolve();
      e.clearSelection();
      clipboard.destroy();
    });
    clipboard.on('error', e => {
      reject();
      clipboard.destroy();
    });
  });
}

// 打开devtools
let logoClickTimes = 0;
let logoClickTimer = 0;
export function openDevTools(e: MouseEvent): void {
  logoClickTimes++;
  if (logoClickTimes >= 10) {
    logoClickTimes = 0;
    if ((e.ctrlKey || e.metaKey) && e.shiftKey) {
      // pc端打开devtools
      ipc.send(IpcEvents.ON_OPERATE_MAIN_LISTENER, {
        event: 'winDevTool',
      });
    } else if (e.shiftKey) {
      throw new Error('test');
    }
  }
  logoClickTimer && window.clearTimeout(logoClickTimer);
  logoClickTimer = window.setTimeout(() => {
    logoClickTimes = 0;
  }, 500);
}

// 打开新页面播放视频 TODO简易版，后面适配pc等
export function openVideo(src: string) {
  const page = window.open();
  const html = `<body>
                  <div style='width:80%;height:80%;margin:auto'>
                    <video controls width='100%' autoplay>
                      <source src='${src}' type="video/mp4"></source>
                    </video>
                  </div>
                </body>`;
  page?.document.write(html);
}

// 客制化的操作者（*拥有所有原子权限的角色）
export function isDisabledAllAuth(role: SMH.CustomRole): boolean {
  let isDisabled = true;
  const auths = [
    'Authorize',
    'Delete',
    'Download',
    'DownloadSelf',
    'Modify',
    'Preview',
    'PreviewSelf',
    'Print',
    'Share',
    'Upload',
    'View',
  ];

  auths.forEach(auth => {
    const key = `can${auth}` as keyof SMH.CustomRole;
    if (role[key]) {
      isDisabled = false;
    }
  });
  return isDisabled;
}
/**
 * @description 获取当前环境对应的官网url
 * @export
 * @return {string} url
 */
export function getOfficialWebsiteUrl() {
  let url = CONSTANT_URL.officialWebsiteUrl;
  if (IS_DEV || IS_DEV_PUBLISH) {
    url = CONSTANT_URL.officialWebsiteUrlDevEnv;
  } else if (IS_TEST) {
    url = CONSTANT_URL.officialWebsiteUrlTestEnv;
  } else if (IS_PRE) {
    url = CONSTANT_URL.officialWebsiteUrlPreEnv;
  }
  return url;
}
/**
 * @description 获取过期天数
 * @export
 * @return {number} expiredDays
 */
export function getExpiredDays(expireTime: string) {
  // 后端没返回过期时间：比如私有化场景下没有过期时间，默认永不过期
  if (!expireTime) return 999;
  // mac会议里不识别2020-01-01需转成兼容的2020/01/01
  const expireTimeTrans = expireTime.replace(/-/g, '/');
  const remainingTime = new Date(expireTimeTrans).getTime() - new Date().getTime();
  return Math.floor(remainingTime / (24 * 3600 * 1000));
}

// 实现sleep效果
export const sleep = (delay: number) => new Promise(resolve => setTimeout(resolve, delay));

export const adminRouterName = [
  'spaceStatistics',
  'userStatistics',
  'trafficStatistics',
  'userManagement',
  'teamManagement',
  'initializeSettings',
  'uiDesign',
  'accountAssociation',
  'watermark',
  'consoleLinkManagement',
  'versionManagement',
  'logQuery',
  'securityScan',
  'customDomain',
];

// 给字符串脱敏，abcdefg=a*****g
export const desensitive = (str: string) => {
  if (!str) return '';
  const len = str.length;
  return `${str[0]}${new Array(len - 2).fill('*').join('')}${str[len - 1]}`;
};

export function getDeviceName(): string {
  let os: typeof import('os');
  if (platform !== 'browser') {
    os = (window.parent as any).require('os') as typeof import('os');
    try {
      // eslint-disable-next-line
      const pkg = require('package.json');
      return `${os.hostname().substr(0, 255)}_${platform}_${pkg.version}`;
    } catch (error) {
      return `${os.hostname().substr(0, 255)}_${platform}`;
    }
  }
  const explorer = window.navigator.userAgent.toLowerCase();
  // 腾讯会议内置浏览器
  if (explorer.includes('app/tencent_wemeet')) {
    return 'tencent_wemeet';
  }
  // TCL 渠道包
  if (explorer.includes('app/tcl_channel')) {
    return 'tencent_tcl';
  }
  // 遨游浏览器
  if (explorer.indexOf('maxthon') >= 0) {
    const match = explorer.match(/maxthon\/([\d.]+)/);
    const ver = (match && match[1]) || '';
    /* i18n-ignore */
    return `傲游浏览器 ${ver}`.trim();
  }
  // QQ浏览器
  if (explorer.indexOf('qqbrowser') >= 0) {
    const match = explorer.match(/qqbrowser\/([\d.]+)/);
    const ver = (match && match[1]) || '';
    /* i18n-ignore */
    return `QQ浏览器 ${ver}`.trim();
  }
  // 搜狗浏览器
  if (explorer.indexOf('se 2.x') >= 0) {
    /* i18n-ignore */
    return '搜狗浏览器';
  }
  // 企微浏览器
  if (explorer.indexOf('wxwork') >= 0) {
    /* i18n-ignore */
    return '企微内置浏览器';
  }
  // 微信浏览器
  if (explorer.indexOf('micromessenger') >= 0) {
    /* i18n-ignore */
    return '微信内置浏览器';
  }
  // ie
  if (explorer.indexOf('msie') >= 0) {
    const match = explorer.match(/msie ([\d.]+)/);
    const ver = (match && match[1]) || '';
    return `IE ${ver}`.trim();
  }
  // firefox
  if (explorer.indexOf('firefox') >= 0) {
    const match = explorer.match(/firefox\/([\d.]+)/);
    const ver = (match && match[1]) || '';
    return `Firefox ${ver}`.trim();
  }
  // Chrome
  if (explorer.indexOf('chrome') >= 0) {
    const match = explorer.match(/chrome\/([\d.]+)/);
    const ver = (match && match[1]) || '';
    return `Chrome ${ver}`.trim();
  }
  // Opera
  if (explorer.indexOf('opera') >= 0) {
    const match = explorer.match(/opera.([\d.]+)/);
    const ver = (match && match[1]) || '';
    return `Opera ${ver}`.trim();
  }
  // Safari
  if (explorer.indexOf('safari') >= 0) {
    const match = explorer.match(/version\/([\d.]+)/);
    const ver = (match && match[1]) || '';
    return `Safari ${ver}`.trim();
  }
  if (explorer.indexOf('edge') >= 0) {
    const match = explorer.match(/edge\/([\d.]+)/);
    const ver = (match && match[1]) || '';
    return `edge ${ver}`.trim();
  }
  return explorer.substr(0, 200);
}

export function getTencentCaptchaLanguage(language: string): string {
  const map: Record<string, string> = {
    'zh-CN': 'zh-cn',
    'en-US': 'en',
  };
  return map[language];
}

export function getWeComAndWeChatLanguage(language: string): string {
  const map: Record<string, string> = {
    'zh-CN': 'cn',
    'en-US': 'en',
  };
  return map[language];
}

/**
 * @description 根据企业名称长度返回对应的状态值，然后显示大、中、小三种字体样式
 * @param orgName 企业名称
 * @param  shortLength  短文案的长度
 * @param middleLength  中等文案长度
 * @return {string} state
 */
export function getOrgNameState(orgName: string, type: string) {
  /**
   * nameType的值可以根据不同场景自己定义
   * 1.场景一：企业名称
   */
  const nameType: { [key: string]: { [key: string]: number } } = {
    orgName: {
      shortLength: 8,
      middleLenght: 20,
    },
  };
  const { shortLength, middleLenght } = nameType[type];
  let len = 0;
  for (let i = 0; i < orgName.length; i++) {
    const str = orgName[i];
    if (isChinese(str)) len += 1;
    else if (isUpperCase(str)) len += 1;
    else len += 0.5;
  }
  let state = 'normal';
  if (len < shortLength) {
    state = 'normal';
  } else if (len >= shortLength && len <= middleLenght) {
    state = 'middle';
  } else {
    state = 'ellipse';
  }
  return state;
}

// ID生成器
export const initId = (prefix = '') => {
  return `${prefix}${new Date().getTime()}${Math.random() * 1000}`;
};
// 错误序列化（可兼容异常对象）
export const errorStringify = (error: any) => {
  let res = '';
  try {
    res = JSON.stringify(error);
  } catch (e) {
    res = i18n_t('错误无法被序列化');
  }
  return res;
};
